// Phase 1B.10.2 — Poisson PMF.
//
// Pure functions only. The ONLY file in src/fair-price/ that may
// contain Math.exp and factorial. The verifier scopes the
// active-code source ban to exclude this file.
//
// poissonPmf(λ, k) = e^(-λ) * λ^k / k!

export const POISSON_FACTORIAL_CACHE_LIMIT = 32;

const factorialCache: number[] = [1]; // factorialCache[0] = 0! = 1
for (let i = 1; i <= POISSON_FACTORIAL_CACHE_LIMIT; i++) {
  factorialCache.push(factorialCache[i - 1]! * i);
}

export function factorial(k: number): number {
  if (!Number.isInteger(k) || k < 0) {
    throw new Error(`factorial: k must be a non-negative integer, got ${k}`);
  }
  if (k <= POISSON_FACTORIAL_CACHE_LIMIT) {
    return factorialCache[k]!;
  }
  // Cold path: extend cache. For our 0..10 grid this never runs.
  let v = factorialCache[POISSON_FACTORIAL_CACHE_LIMIT]!;
  for (let i = POISSON_FACTORIAL_CACHE_LIMIT + 1; i <= k; i++) v *= i;
  return v;
}

// Probability mass function for Poisson(λ) at k.
//   λ >= 0, k integer >= 0.
//   P(X = k) = e^(-λ) * λ^k / k!
//   λ = 0  → P(X = 0) = 1, P(X > 0) = 0.
export function poissonPmf(lambda: number, k: number): number {
  if (!Number.isFinite(lambda) || lambda < 0) {
    throw new Error(`poissonPmf: lambda must be finite and >= 0, got ${lambda}`);
  }
  if (!Number.isInteger(k) || k < 0) {
    throw new Error(`poissonPmf: k must be a non-negative integer, got ${k}`);
  }
  if (lambda === 0) return k === 0 ? 1 : 0;
  // Stable computation: e^(-λ) * λ^k / k!
  return (Math.exp(-lambda) * Math.pow(lambda, k)) / factorial(k);
}
